import { Vector3 } from "@react-three/fiber";

// The distance from which the camera is pointing to the phone.
export const CAMERA_DISTANCE = 2.5;

// A small number to avoid z-index flickering
export const Z_FLICKER_PREVENTION = 0.001;

// Shininess of the phone
export const PHONE_SHININESS = 30;

// In how many segments the phone rounded corners
// are divided. Increase number for smoother phone
export const PHONE_CURVE_SEGMENTS = 8;

// X-axis rotation applied to the phone (and its screen). -π/2 lays the
// phone fully flat (face up); 0 keeps it fully upright. -π/4 (45°)
// matches the timer's settled "face the camera" angle so all three
// elements (phone, whiteboard, timer) share the same flat-view tilt.
export const PHONE_LAY_TILT = -Math.PI / 4;

// World-space position of a point on the phone screen surface, accounting
// for PHONE_LAY_TILT. `screenLocalY` selects the position along the
// screen's vertical axis (in the phone's local frame): 0 = screen center,
// positive = toward the top of the screen, negative = toward the bottom.
// Whiteboard fly-in landing and TimerPopUp pop-up start both rely on
// this — keeping the rotation trig in one place means tweaking the tilt
// angle doesn't require manual math in each consumer.
export const getPhoneScreenWorldPosition = (
  phoneLayout: PhoneLayout,
  worldOffset: [number, number, number],
  screenLocalY = 0,
): [number, number, number] => {
  // Local screen surface point (0, screenLocalY, thickness) inside the
  // phone tilt group. X-axis rotation by θ maps:
  //   (0, y, z) → (0, y·cosθ - z·sinθ, y·sinθ + z·cosθ).
  const t = phoneLayout.phone.thickness + Z_FLICKER_PREVENTION;
  const cos = Math.cos(PHONE_LAY_TILT);
  const sin = Math.sin(PHONE_LAY_TILT);
  return [
    worldOffset[0],
    screenLocalY * cos - t * sin + worldOffset[1],
    screenLocalY * sin + t * cos + worldOffset[2],
  ];
};

// Calculate phone size. Whichever side is smaller gets
// normalized to the base scale.
const getPhoneHeight = (aspectRatio: number, baseScale: number): number => {
  if (aspectRatio > 1) {
    return baseScale;
  }
  return baseScale / aspectRatio;
};

const getPhoneWidth = (aspectRatio: number, baseScale: number): number => {
  if (aspectRatio < 1) {
    return baseScale;
  }
  return baseScale * aspectRatio;
};

type Layout = {
  position: Vector3;
  height: number;
  width: number;
  radius: number;
};

export type PhoneLayout = {
  phone: Layout & {
    thickness: number;
    bevel: number;
  };
  screen: Layout;
};

export const getPhoneLayout = (
  // I recommend building the phone layout based
  // on the aspect ratio of the phone
  aspectRatio: number,
  // This value can be increased or decreased to tweak the
  // base value of the phone.
  baseScale: number,
): PhoneLayout => {
  // The depth of the phone body
  const phoneThickness = baseScale * 0.15;

  // How big the border of the phone is.
  const phoneBevel = baseScale * 0.04;

  // The inner radius of the phone, aka the screen radius
  const screenRadius = baseScale * 0.07;

  const phoneHeight = getPhoneHeight(aspectRatio, baseScale);
  const phoneWidth = getPhoneWidth(aspectRatio, baseScale);
  const phonePosition: Vector3 = [-phoneWidth / 2, -phoneHeight / 2, 0];
  const screenWidth = phoneWidth - phoneBevel * 2;
  const screenHeight = phoneHeight - phoneBevel * 2;
  const screenPosition: Vector3 = [
    -screenWidth / 2,
    -screenHeight / 2,
    phoneThickness + Z_FLICKER_PREVENTION,
  ];

  // Define the outer radius of the phone.
  // It looks better if the outer radius is a bit bigger than the screen radios,
  // formula taken from https://twitter.com/joshwcomeau/status/134978208002102886
  const phoneRadius =
    screenRadius + (getPhoneWidth(aspectRatio, baseScale) - screenWidth) / 2;

  return {
    phone: {
      position: phonePosition,
      height: phoneHeight,
      width: phoneWidth,
      radius: phoneRadius,
      thickness: phoneThickness,
      bevel: phoneBevel,
    },
    screen: {
      position: screenPosition,
      height: screenHeight,
      width: screenWidth,
      radius: screenRadius,
    },
  };
};

export type WhiteboardLayout = {
  width: number;
  height: number;
  thickness: number;
  radius: number;
};

// Whiteboard intrinsic dimensions. Scene-level positions (settled vs.
// off-screen) live below as separate constants; the slide-in motion in
// Whiteboard.tsx interpolates between them frame-by-frame.
export const getWhiteboardLayout = (baseScale: number): WhiteboardLayout => ({
  width: baseScale * 0.74,
  height: baseScale * 0.5,
  thickness: baseScale * 0.04,
  radius: baseScale * 0.05,
});

// Settled phone offset in world space — strictly at scene center. The
// whiteboard docks to its LEFT (and stays there even after the
// screenshot is taken), so the phone is the visual anchor. Pulled
// slightly back in Z so the whiteboard, which sits forward in Z, layers
// cleanly in front of it.
export const getPhoneSettledOffset = (
  baseScale: number,
): [number, number, number] => [0.65, 0, -baseScale * 0.15];

// Phone slide-in start: well off the left edge so the phone glides in
// from completely outside the frame.
export const getPhoneOffscreenOffset = (
  baseScale: number,
): [number, number, number] => [-baseScale * 3.5, 0, -baseScale * 0.15];

// Whiteboard settled position — same X as before (LEFT of the phone),
// just lifted higher in Y so it sits noticeably above the phone instead
// of overlapping its upper-left corner. Z still pushes the board forward
// in front of the phone.
export const getWhiteboardSettledPosition = (
  baseScale: number,
): [number, number, number] => [-baseScale * 0.45, baseScale * 0.65, baseScale * 1.0];

// Whiteboard slide-IN start — off-screen RIGHT on the same Y/Z plane as
// the settled position so the slide is purely horizontal across the
// upper portion of the frame.
export const getWhiteboardSlideInStartPosition = (
  baseScale: number,
): [number, number, number] => [baseScale * 3.5, baseScale * 1.0, baseScale * 1.0];

// Whiteboard tilt: -π/4 to match the phone's tilt and the timer's
// settled rotation — all three elements present a consistent "flat
// view" angle to the camera.
export const WHITEBOARD_TILT: [number, number, number] = [
  -Math.PI / 4,
  0,
  0,
];
